import React, { useEffect, useState } from "react";
import Form from "../../../form/Form";
import Card from "../../../elements/Card";
import Input from "../../../form/Input";
import Select from "../../../form/select/Select";
import Button from "../../../form/Button";
import Image from "../../../elements/Image";
import Tooltip from "../../../elements/Tooltip";
import PhoneInput from "../../../form/PhoneInput";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { countryList } from "../../../../services/countryService";
import { loadDataOnScroll, replaceOldData } from "../../../../helpers";
import useForm from "../../../../hooks/useForm";
import {
  profileDetails,
  profileImageDelete,
  profileUpdate,
} from "../../../../services/ProfileService";
import DeleteModal from "../../../common/DeleteModal";
import dayjs from "dayjs";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useDispatch } from "react-redux";
import { setSession } from "../../../../redux/slice/authSlice";
import { toast } from "react-toastify";
import { t } from "i18next";

const ContactInfo = () => {
  const queryClient = useQueryClient();
  const [countryFilter, setCountryFilter] = useState(null);
  const [countryKeyword, setCountryKeyword] = useState("");
  const countrySearch = useDebounce(countryKeyword, 300);
  const { values, handleChange, handleSubmit, errors, setFieldsValue } =
    useForm({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dispatch = useDispatch();

  const { data: profileData } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const res = await profileDetails();
      return res;
    },
  });

  useEffect(() => {
    if (profileData?.data?._id) {
      setFieldsValue({
        phone: profileData?.data?.phone,
        firstName: profileData?.data?.firstName,
        lastName: profileData?.data?.lastName,
        email: profileData?.data?.email,
        image: profileData?.data?.image,
        address1: profileData?.data?.address?.[0]?.address1,
        address2: profileData?.data?.address?.[0]?.address2,
        city: profileData?.data?.address?.[0]?.city,
        zipcode: profileData?.data?.address?.[0]?.zipcode,
        state: profileData?.data?.address?.[0]?.state,
        country: profileData?.data?.address?.[0]?.country,
      });
    }
  }, [profileData?.data]);

  const { data: countryData } = useQuery({
    queryKey: ["country", countryFilter, countrySearch],
    queryFn: async () => {
      const res = await countryList({
        ...countryFilter,
        ...(countrySearch && { keyword: countrySearch }),
      });

      if (countryFilter?.page > 1 && !countrySearch) {
        const marge_arr = [...(countryData?.data || []), ...res?.docs?.data];
        return {
          ...res?.docs,
          data: replaceOldData({
            oldData: marge_arr,
            newData: res?.docs?.data,
          }),
        };
      } else {
        return { ...res?.docs };
      }
    },
    placeholderData: keepPreviousData,
  });

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: async (value) => {
      const payload = {
        ...value,
        address: [
          {
            address1: value?.address1,
            address2: value?.address2,
            city: value?.city,
            zipcode: value?.zipcode,
            state: value?.state,
            country: value?.country,
          },
        ],
      };
      delete payload?.address1;
      delete payload?.address2;
      delete payload?.city;
      delete payload?.zipcode;
      delete payload?.state;
      delete payload?.country;
      return await profileUpdate(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        queryClient.setQueryData(["profile"], (state) => {
          const state_data = JSON.parse(JSON.stringify(state || {}));
          state_data.data = res?.data;
          return { ...state_data };
        });
        dispatch(
          setSession({
            user: { ...res?.data },
            status: "authorized",
            error: null,
          })
        );
      } else {
        toast.error(res?.message || res?.error);
      }
    },
  });

  const { mutate: removeImageHandler, isPending: imageRemovePending } =
    useMutation({
      mutationFn: async (img) => {
        return await profileImageDelete();
      },
      onSuccess: (res) => {
        if (res?.status === 200) {
          setShowDeleteModal(false);
          queryClient.setQueryData(["profile"], (state) => {
            const state_data = JSON.parse(JSON.stringify(state || {}));
            state_data.data = { ...state_data?.data, image: { url: null } };
            return { ...state_data };
          });
        }
      },
    });

  return (
    <>
      <Form handleSubmit={handleSubmit} onSubmit={onSubmit}>
        <div className="bg-white py-4 px-4 rounded-md w-full">
          <div className="flex flex-col md:flex-row gap-5 items-start">
            <Card
              title={t("Profile photo")}
              bodyClass={"space-y-6"}
              className={
                "w-full md:w-[45%] lg:w-1/2 xl:w-1/3 py-6 md:sticky md:top-28 px-2 lg:px-6"
              }
            >
              <div className="flex flex-col items-start gap-3">
                <label className="relative w-full aspect-square rounded-lg overflow-hidden border border-gray-100">
                  <Image
                    src={
                      values?.image
                        ? values?.image instanceof File
                          ? URL.createObjectURL(values?.image)
                          : values?.image?.url ||
                            require("../../../../../src/assets/images/dummy-user.png")
                        : require("../../../../../src/assets/images/dummy-user.png")
                    }
                    alt="Uploaded Image"
                    classes="object-cover"
                  />
                  <input
                    type="file"
                    className="h-0 w-0 opacity-0 absolute top-0 left-0"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      const type = file?.type?.split("/")[0];
                      if (type === "image") {
                        handleChange({
                          target: { name: "image", value: file },
                        });
                      }
                      e.target.value = "";
                    }}
                  />
                </label>

                <div className="flex gap-4 justify-end pt-1 w-full">
                  <div className="flex gap-3 w-full justify-end">
                    <Tooltip
                      message={t("Delete this photo")}
                      className={"w-full"}
                    >
                      <Button
                        buttonClasses="w-full bg-red-50 text-red-600 hover:bg-red-100 rounded-md px-3 py-2 text-sm font-medium shadow-sm transition"
                        buttonIcon="fa-light fa-trash-can"
                        buttonIconPosition="left"
                        buttonLabel={t("Delete")}
                        buttonFunction={() => {
                          if (values?.image?.url) {
                            setShowDeleteModal(true);
                          } else {
                            setFieldsValue({ image: null });
                          }
                          // removeImageHandler(values?.image);
                        }}
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-1  border-t border-gray-200 pt-4">
                <div className="text-sm text-gray-600 flex-shrink">
                  {t("dancebrite account since")}{" "}
                  {dayjs(profileData?.data?.createdAt).format("DD MMM, YYYY")}
                </div>
              </div>
            </Card>
            <div className="w-full space-y-5">
              <Card
                title={t("Contact information")}
                bodyClass={"space-y-6"}
                className={"py-6 px-3 md:px-6"}
              >
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 xl:col-span-6">
                    <div className="relative">
                      <Input
                        value={values?.firstName || ""}
                        inputName={"firstName"}
                        label={t("First name")}
                        labelClasses={"!text-lg !text-deep-purple !mb-2"}
                        inputClasses={
                          "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                        }
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-span-12 xl:col-span-6">
                    <div className="relative">
                      <Input
                        value={values?.lastName || ""}
                        inputName={"lastName"}
                        label={t("Last name")}
                        labelClasses={"!text-lg !text-deep-purple !mb-2"}
                        inputClasses={
                          "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                        }
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-span-12 xl:col-span-6">
                    <div className="relative">
                      <Input
                        label={t("Email")}
                        inputType="email"
                        isDisabled={true}
                        value={values?.email || ""}
                        labelClasses={"!text-lg !text-deep-purple !mb-2"}
                        inputClasses={
                          "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                        }
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-span-12 xl:col-span-6">
                    <div className="relative">
                      <PhoneInput
                        label={t("Phone")}
                        labelClasses={"!text-lg !text-deep-purple !mb-2"}
                        onChange={(val) => {
                          handleChange({
                            target: {
                              name: "phone",
                              value: {
                                ...val,
                                fullNumber:
                                  `${val?.countryCode || ""} ${
                                    val?.number || ""
                                  }` || "",
                              },
                            },
                          });
                        }}
                        value={values?.phone}
                      />
                    </div>
                  </div>
                  {/* <div className="col-span-12 xl:col-span-6">
                <div className="relative">
                  <Input
                    label={"Job Title"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <div className="relative">
                  <Input
                    label={"Company / Organization"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <div className="relative">
                  <Input
                    label={"Website"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <div className="relative">
                  <Input
                    label={"Blog"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                  />
                </div>
              </div> */}
                </div>
              </Card>
              <Card
                title={t("Address")}
                bodyClass={"space-y-6"}
                className={"py-6 px-3 md:px-6"}
              >
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12">
                    <div className="relative">
                      <Input
                        label={t("Address")}
                        labelClasses={"!text-lg !text-deep-purple !mb-2"}
                        inputClasses={
                          "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                        }
                        inputName={"address1"}
                        value={values?.address1 || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-span-12">
                    <div className="relative">
                      <Input
                        label={t("Address 2")}
                        labelClasses={"!text-lg !text-deep-purple !mb-2"}
                        inputClasses={
                          "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                        }
                        inputName={"address2"}
                        value={values?.address2 || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-span-12 xl:col-span-6">
                    <div className="relative">
                      <Input
                        label={t("City")}
                        labelClasses={"!text-lg !text-deep-purple !mb-2"}
                        inputClasses={
                          "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                        }
                        inputName={"city"}
                        value={values?.city || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-span-12 xl:col-span-6">
                    <div className="relative">
                      <Input
                        label={t("Zipcode")}
                        inputName={"zipcode"}
                        value={values?.zipcode || ""}
                        labelClasses={"!text-lg !text-deep-purple !mb-2"}
                        inputClasses={
                          "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                        }
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-span-12 xl:col-span-6">
                    <div className="relative">
                      <Input
                        label={t("State")}
                        inputName={"state"}
                        value={values?.state || ""}
                        labelClasses={"!text-lg !text-deep-purple !mb-2"}
                        inputClasses={
                          "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                        }
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="col-span-12 xl:col-span-6">
                    <Select
                      dropdownData={[
                        ...(profileData?.data?.address?.[0]?.country
                          ? [
                              {
                                name: profileData.data.address[0].country,
                                value: profileData.data.address[0].country,
                              },
                            ]
                          : []),
                        ...(countryData?.data || [])
                          .filter(
                            (item) =>
                              !profileData?.data?.address?.[0]?.country ||
                              item?.name !== profileData.data.address[0].country
                          )
                          .map((item) => ({
                            name: item?.name,
                            value: item?.name,
                          })),
                      ]}
                      selectedValue={values?.country || ""}
                      onChange={(val, fullItem) => {
                        handleChange({
                          target: { name: "country", value: val },
                        });
                      }}
                      isSearch
                      onSearchHandler={(val) => {
                        setCountryKeyword(val);
                        setCountryFilter((pre) => ({ ...pre, page: 1 }));
                      }}
                      search={countryKeyword}
                      placeholder="--"
                      label={t("Select a country")}
                      labelClasses={"text-lg text-deep-purple mb-2"}
                      dropdownClass={"w-full"}
                      dropdownButtonClass={"rounded-md !border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"}
                      onScroll={(e) => {
                        loadDataOnScroll({
                          event: e,
                          metadata: countryData?.metadata,
                          setData: setCountryFilter,
                        });
                      }}
                    />
                  </div>
                </div>
              </Card>
            </div>
            {/* <Card title="Billing Address" bodyClass={"space-y-6"}>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12">
                <div className="relative">
                  <Input
                    label={"Address"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                    inputName={"title"}
                  />
                </div>
              </div>
              <div className="col-span-12">
                <div className="relative">
                  <Input
                    label={"Address 2"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <div className="relative">
                  <Input
                    label={"City"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                    inputName={"title"}
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <div className="relative">
                  <Input
                    label={"Postcode"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <div className="relative">
                  <Select
                    dropdownData={stateData}
                    prefix={state}
                    onChange={(val, fullItem) => {
                      setstate(val);
                    }}
                    placeholder="--"
                    label="State"
                    labelClasses={"text-lg text-deep-purple mb-2"}
                    dropdownClass={"w-full"}
                    dropdownButtonClass={"rounded-md"}
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <Select
                  dropdownData={countryData}
                  prefix={country}
                  onChange={(val, fullItem) => {
                    setcountry(val);
                  }}
                  placeholder="--"
                  label="Select a country"
                  labelClasses={"text-lg text-deep-purple mb-2"}
                  dropdownClass={"w-full"}
                  dropdownButtonClass={"rounded-md"}
                />
              </div>
            </div>
          </Card>
          <Card title="Work Address" bodyClass={"space-y-6"}>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12">
                <div className="relative">
                  <Input
                    label={"Address"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                    inputName={"title"}
                  />
                </div>
              </div>
              <div className="col-span-12">
                <div className="relative">
                  <Input
                    label={"Address 2"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <div className="relative">
                  <Input
                    label={"City"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                    inputName={"title"}
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <div className="relative">
                  <Input
                    label={"Postcode"}
                    labelClasses={"!text-lg !text-deep-purple !mb-2"}
                    inputClasses={
                      "!border-surface-05 !bg-surface-04 !h-12 !font-medium !text-deep-purple"
                    }
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <div className="relative">
                  <Select
                    dropdownData={stateData}
                    prefix={state}
                    onChange={(val, fullItem) => {
                      setstate(val);
                    }}
                    placeholder="--"
                    label="State"
                    labelClasses={"text-lg text-deep-purple mb-2"}
                    dropdownClass={"w-full"}
                    dropdownButtonClass={"rounded-md"}
                  />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-6">
                <Select
                  dropdownData={countryData}
                  prefix={country}
                  onChange={(val, fullItem) => {
                    setcountry(val);
                  }}
                  placeholder="--"
                  label="Select a country"
                  labelClasses={"text-lg text-deep-purple mb-2"}
                  dropdownClass={"w-full"}
                  dropdownButtonClass={"rounded-md"}
                />
              </div>
            </div>
          </Card> */}
          </div>
        </div>
        <div className="flex gap-3 justify-end pb-10 pr-4">
          <Button
            buttonLabel={t("Save")}
            buttonType="submit"
            {...(isPending && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
        </div>
      </Form>
      <DeleteModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
        }}
        deleteHandler={() => removeImageHandler(values?.image)}
        customMessage={"Are you sure you want to delete this image?"}
        loading={imageRemovePending}
      />
    </>
  );
};

export default ContactInfo;
